import { redirect } from "@tanstack/react-router";
import { useStore } from "@/store/store";
import type { TUserDataSchema, TUserRoleGroup } from "@/api/user";
type TFeatureFlag = string;
type TUserLike =
  (TUserDataSchema & { selectedRole?: string }) | null | undefined;

type TRouteConfig = {
  feature: TFeatureFlag;
  path: string;
  permissions?: string[];
  requireAll?: boolean;
  resolvePath?: (user: TUserDataSchema) => string; // Optional function to resolve dynamic params
};
//All routes array
const ALL_FIRST_ROUTES: TRouteConfig[] = [
  {
    feature: "evaluation-dashboard",
    path: "/evaluation-dashboard",
    permissions: ["can-view-evaluation-dashboard"],
  },
  {
    feature: "agent-performance-dashboard",
    path: "/agent-performance-details/$agentId",
    permissions: ["can-view-agent-performance-report-details"],
    resolvePath: (user) =>
      "/agent-performance-details/" + (user?.employee?.internal_id || ""),
  },
  {
    feature: "evaluation-list",
    path: "/evaluation-listing",
    permissions: ["can-view-evaluation-list"],
  },
  {
    feature: "template",
    path: "/manage-template",
    permissions: ["can-view-evaluation-templates"],
  },
  {
    feature: "users",
    path: "/manage-users",
    permissions: ["can-view-manage-users"],
  },
  {
    feature: "employee-management",
    path: "/employee-management",
    permissions: ["can-view-employee-list"],
  },
  {
    feature: "nano-learning",
    path: "/learning",
    permissions: [
      "can-view-nano-learning",
      "can-view-nano-learning-browse-content",
    ],
  },
  {
    feature: "nano-learning",
    path: "/nano-learning",
    permissions: [
      "can-view-nano-learning",
      "can-view-nano-learning-create-module",
    ],
    requireAll: true,
  },
  { feature: "reports", path: "/reports", permissions: ["can-view-reports"] },

  {
    feature: "configurations",
    path: "/configurations",
    permissions: ["can-view-configurations"],
  },
];

export const isAdminLike = (user: TUserLike): boolean => {
  if (!user) return false;

  if (user.is_superuser === true || user.is_staff === true) {
    return true;
  }

  const roleGroup = user.role_group as
    TUserRoleGroup | TUserRoleGroup[] | null | undefined;

  if (!roleGroup) return false;

  if (Array.isArray(roleGroup)) {
    return roleGroup.some((role) =>
      role?.name?.toLowerCase().includes("admin"),
    );
  }

  return roleGroup.name?.toLowerCase().includes("admin");
};

// checking for params feature exisitance in main feature array.
const hasFeature = (
  featureFlags: TFeatureFlag[] | undefined,
  feature: string,
) => Array.isArray(featureFlags) && featureFlags.includes(feature);

// checking specific route permissions are available in main user assigned permissions
const routeHasRequiredPermissions = (
  route: TRouteConfig,
  userPermissions: string[] | null | undefined,
): boolean => {
  if (!route.permissions || route.permissions.length === 0) {
    return true;
  }
  const permissions = route?.permissions;
  return route?.requireAll
    ? permissions?.every((p) => userPermissions?.includes(p))
    : permissions?.some((p) => userPermissions?.includes(p));
};

/**
 * Resolve the first landing page for the given user based on:
 * - Role (admin vs non-admin)
 * - Feature flags from the store
 * - Permissions on the active role
 */
export const resolveLandingPage = (user: TUserDataSchema): string => {
  if (!user) return "/un-authorized";

  const featureFlags = useStore.getState()?.featureShowHideFlag as
    TFeatureFlag[] | undefined;

  const isAdmin = isAdminLike(user);
  if (isAdmin) {
    for (const route of ALL_FIRST_ROUTES) {
      if (hasFeature(featureFlags, route.feature)) {
        return route.resolvePath ? route.resolvePath(user) : route.path;
      }
    }
    // Admin but no matching feature -> fallback to dashboard.
    return "/evaluation-dashboard";
  }
  const useRole = user?.role_group[0];
  for (const route of ALL_FIRST_ROUTES) {
    if (!hasFeature(featureFlags, route.feature)) continue;
    if (!routeHasRequiredPermissions(route, useRole?.permissions)) continue;
    return route.resolvePath ? route.resolvePath(user) : route.path;
  }
  return "/forbidden";
};

export const ROUTES = {
  APP: "/app",
  ROLE_SELECT: "/auth/role-select",
  UPDATE_PASSWORD: "/auth/update-password",
} as const;

/**
 * Resolves redirect path and updates store for single-role users.
 * Returns ROUTES.ROLE_SELECT, ROUTES.UPDATE_PASSWORD, or null if no redirect needed.
 */
export function resolveAuthRedirect(
  user: TUserDataSchema | null | undefined,
): string | null {
  if (!user?.role_group) return null;

  // if (user.is_first_password_updated === false) {
  //   return ROUTES.UPDATE_PASSWORD;
  // }

  const roleGroup = user.role_group;
  const isMultipleRoles = Array.isArray(roleGroup) && roleGroup.length > 1;

  if (isMultipleRoles) {
    return ROUTES.ROLE_SELECT;
  }
  return null;
}

/**
 * Route beforeLoad helper: ensures at least one of requiredSlugs is in feature flags.
 * If not authorized, redirects to user's landing page when user exists, else /un-authorized.
 */
export const routesFeatureFlagCheck = (requiredSlugs: string[]) => {
  const featureFlags = useStore?.getState()?.featureShowHideFlag;
  // const user = useStore?.getState()?.user;

  if (!Array.isArray(featureFlags) || featureFlags.length === 0) {
    return;
  }

  const isAuthorized = requiredSlugs?.some((slug) =>
    featureFlags?.includes(slug),
  );
  if (!isAuthorized) {
    redirect({
      to: "/forbidden",
      throw: true,
    });
  }
};
